import { useSEO } from '../../../utils/seo';

export default function HowToVetContractorIdaho() {
  useSEO({
    title: 'How to Vet a Contractor in Idaho | Complete Background Check Guide',
    description: 'Learn how to thoroughly vet contractors in Idaho before hiring. Verify licenses, check insurance, review references, and avoid scams. Complete guide for Boise homeowners.',
    keywords: 'vet contractor Idaho, verify contractor license Boise, check contractor insurance Idaho, contractor background check Treasure Valley, Idaho contractor verification',
    canonical: `${import.meta.env.VITE_SITE_URL}/blog/how-to-vet-contractor-idaho`,
    ogType: 'article',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'How to Vet a Contractor in Idaho | Complete Background Check Guide',
          description: 'Learn how to thoroughly vet contractors in Idaho before hiring. Verify licenses, check insurance, review references, and avoid scams. Complete guide for Boise homeowners.',
          author: {
            '@type': 'Organization',
            name: 'Fix Right Services'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Fix Right Services',
            logo: {
              '@type': 'ImageObject',
              url: `${import.meta.env.VITE_SITE_URL}/images/logo.svg`
            }
          },
          datePublished: '2025-01-15',
          dateModified: '2025-01-15'
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: import.meta.env.VITE_SITE_URL
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${import.meta.env.VITE_SITE_URL}/blog`
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'How to Vet a Contractor in Idaho',
              item: `${import.meta.env.VITE_SITE_URL}/blog/how-to-vet-contractor-idaho`
            }
          ]
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How do I verify a contractor license in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Visit ibol.idaho.gov and search by contractor name or license number. Verify the license is active, check for any disciplinary actions, and confirm it matches the contractor\'s specialty (RCE for residential, RCT for trades). You can also call (208) 334-3233 for verification.'
              }
            },
            {
              '@type': 'Question',
              name: 'What insurance should a contractor have in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Idaho contractors must carry: General Liability Insurance (minimum $300,000 recommended), Workers\' Compensation (if they have employees), and Commercial Auto Insurance (for work vehicles). Always request certificates of insurance and verify coverage is current.'
              }
            },
            {
              '@type': 'Question',
              name: 'How many references should I check before hiring a contractor?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Check at least 3-5 references from recent projects (within the last 12 months). Ask specific questions about quality, timeline, communication, budget adherence, and problem-solving. Also check online reviews on Google, Yelp, and Better Business Bureau.'
              }
            },
            {
              '@type': 'Question',
              name: 'What are red flags when vetting a Boise contractor?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Red flags include: no valid license, no insurance proof, requesting large upfront payments (50%+), no written contract, pressure tactics, no physical address, cash-only payments, no references, and unwillingness to pull permits. Any of these should make you walk away.'
              }
            },
            {
              '@type': 'Question',
              name: 'Should I check a contractor\'s Better Business Bureau rating?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, check BBB ratings at bbb.org. Look for: overall rating (A+ to F), number of complaints, how complaints were resolved, years in business, and any patterns in negative reviews. However, also check Google and Yelp for a complete picture.'
              }
            },
            {
              '@type': 'Question',
              name: 'How do I verify a contractor\'s insurance in Idaho?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Request a Certificate of Insurance (COI) directly from the contractor. Call the insurance company listed to verify coverage is active and adequate. The COI should list you as an additional insured for the project duration. Never accept expired or photocopied certificates.'
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <article className="max-w-4xl mx-auto px-6 py-16">
        <header className="mb-12">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <a href="/" className="hover:text-teal-600 transition-colors">Home</a>
            <span>/</span>
            <a href="/blog" className="hover:text-teal-600 transition-colors">Blog</a>
            <span>/</span>
            <span className="text-gray-900">How to Vet a Contractor in Idaho</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            How to Vet a Contractor in Idaho | Complete Background Check Guide
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <time dateTime="2025-01-15">January 15, 2025</time>
            <span>•</span>
            <span>9 min read</span>
          </div>
        </header>

        <div className="prose prose-lg max-w-none">
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            Hiring the wrong contractor can cost <strong>Idaho homeowners</strong> thousands of dollars and months of frustration. Whether you're planning a <strong>fence installation in Boise</strong>, <strong>kitchen remodel</strong>, or <strong>handyman repairs</strong>, thoroughly vetting contractors before signing a contract is essential. This comprehensive guide shows you exactly how to verify credentials, check references, and avoid scams in the <strong>Treasure Valley</strong>.
          </p>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Complete Contractor Vetting Checklist</h2>
          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">Essential Steps (Complete All Before Hiring):</h3>
            <ul className="space-y-2 text-gray-700">
              <li>☐ Verify Idaho contractor license</li>
              <li>☐ Confirm insurance coverage (liability + workers' comp)</li>
              <li>☐ Check Better Business Bureau rating</li>
              <li>☐ Read online reviews (Google, Yelp, Facebook)</li>
              <li>☐ Contact 3-5 recent references</li>
              <li>☐ Verify physical business address</li>
              <li>☐ Review sample contracts and warranties</li>
              <li>☐ Check for liens or legal issues</li>
              <li>☐ Confirm permit-pulling capabilities</li>
              <li>☐ Assess communication and professionalism</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 1: Verify Idaho Contractor License</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Idaho requires contractors to be licensed for most home improvement work. Here's how to verify:
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How to Check License Status</h3>
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <ol className="space-y-3 text-gray-700">
              <li><strong>1. Visit the Idaho Bureau of Occupational Licenses:</strong> Go to <a href="https://ibol.idaho.gov" className="text-teal-600 hover:underline" target="_blank" rel="noopener noreferrer">ibol.idaho.gov</a></li>
              <li><strong>2. Search by name or license number:</strong> Use the contractor lookup tool</li>
              <li><strong>3. Verify license is active:</strong> Check expiration date and status</li>
              <li><strong>4. Check for disciplinary actions:</strong> Review any complaints or violations</li>
              <li><strong>5. Confirm specialty matches your project:</strong> RCE (residential), RCT (trades), etc.</li>
              <li><strong>6. Call to verify:</strong> (208) 334-3233 for phone verification</li>
            </ol>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Idaho License Types</h3>
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">License Type</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Abbreviation</th>
                  <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">What They Can Do</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Residential Contractor</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">RCE</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">General residential construction and remodeling</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Residential Tradesman</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">RCT</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specific trades (plumbing, electrical, HVAC)</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Public Works Contractor</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">PWC</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Commercial and public projects</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specialty Contractor</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Various</td>
                  <td className="border border-gray-300 px-4 py-3 text-gray-700">Specific specialties (roofing, siding, etc.)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 2: Verify Insurance Coverage</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Proper insurance protects you from liability if accidents or damage occur during your project.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Required Insurance Types</h3>
          <div className="space-y-6 mb-8">
            <div className="border-l-4 border-blue-600 pl-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">General Liability Insurance</h4>
              <p className="text-gray-700 mb-2"><strong>Minimum recommended:</strong> $300,000 coverage</p>
              <p className="text-gray-700 mb-2"><strong>Covers:</strong> Property damage, bodily injury, legal fees</p>
              <p className="text-gray-700"><strong>Why it matters:</strong> Protects your home if contractor damages it</p>
            </div>

            <div className="border-l-4 border-blue-600 pl-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Workers' Compensation Insurance</h4>
              <p className="text-gray-700 mb-2"><strong>Required if:</strong> Contractor has employees</p>
              <p className="text-gray-700 mb-2"><strong>Covers:</strong> Employee injuries on your property</p>
              <p className="text-gray-700"><strong>Why it matters:</strong> Without it, you could be liable for worker injuries</p>
            </div>

            <div className="border-l-4 border-blue-600 pl-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">Commercial Auto Insurance</h4>
              <p className="text-gray-700 mb-2"><strong>Required if:</strong> Contractor uses vehicles for work</p>
              <p className="text-gray-700 mb-2"><strong>Covers:</strong> Vehicle accidents during project</p>
              <p className="text-gray-700"><strong>Why it matters:</strong> Protects against vehicle-related damage</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How to Verify Insurance</h3>
          <ol className="list-decimal pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Request Certificate of Insurance (COI):</strong> Ask contractor to provide current COI</li>
            <li><strong>Call the insurance company:</strong> Verify coverage is active and amounts are accurate</li>
            <li><strong>Check expiration dates:</strong> Ensure coverage extends through project completion</li>
            <li><strong>Verify you're listed as additional insured:</strong> Should be added for project duration</li>
            <li><strong>Get it in writing:</strong> Never accept verbal assurances about insurance</li>
          </ol>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 3: Check Online Reviews and Ratings</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Online reviews provide real feedback from past clients. Check multiple platforms for a complete picture:
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-3">Where to Check:</h4>
              <ul className="space-y-2 text-gray-700">
                <li>✓ <strong>Google Reviews:</strong> Most comprehensive</li>
                <li>✓ <strong>Better Business Bureau:</strong> Complaint history</li>
                <li>✓ <strong>Yelp:</strong> Detailed customer experiences</li>
                <li>✓ <strong>Facebook:</strong> Community feedback</li>
                <li>✓ <strong>Angi (Angie's List):</strong> Verified reviews</li>
                <li>✓ <strong>HomeAdvisor:</strong> Project-specific feedback</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-3">What to Look For:</h4>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Overall rating (4+ stars ideal)</li>
                <li>✓ Number of reviews (20+ preferred)</li>
                <li>✓ Recent reviews (within 6 months)</li>
                <li>✓ Response to negative reviews</li>
                <li>✓ Patterns in complaints</li>
                <li>✓ Specific project details</li>
              </ul>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How to Spot Fake Reviews</h3>
          <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
            <h4 className="font-bold text-gray-900 mb-3">Red Flags for Fake Reviews:</h4>
            <ul className="space-y-2 text-gray-700">
              <li>🚩 All 5-star reviews with no negatives</li>
              <li>🚩 Generic language ("Great service!", "Highly recommend!")</li>
              <li>🚩 Multiple reviews posted on same day</li>
              <li>🚩 Reviewers with no other review history</li>
              <li>🚩 Overly promotional language</li>
              <li>🚩 No specific project details mentioned</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 4: Contact References</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Speaking directly with past clients provides invaluable insights. Request at least 3-5 references from recent projects (within the last 12 months).
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Questions to Ask References</h3>
          <div className="bg-teal-50 rounded-lg p-6 mb-8">
            <h4 className="font-bold text-gray-900 mb-4">Essential Reference Questions:</h4>
            <ol className="space-y-3 text-gray-700">
              <li><strong>1. What type of project did they complete for you?</strong></li>
              <li><strong>2. Was the work completed on time and on budget?</strong></li>
              <li><strong>3. How was their communication throughout the project?</strong></li>
              <li><strong>4. Were there any unexpected issues? How did they handle them?</strong></li>
              <li><strong>5. How was the quality of workmanship?</strong></li>
              <li><strong>6. Did they clean up properly after each day?</strong></li>
              <li><strong>7. Were workers professional and respectful?</strong></li>
              <li><strong>8. Have you had any issues since project completion?</strong></li>
              <li><strong>9. Did they honor their warranty?</strong></li>
              <li><strong>10. Would you hire them again?</strong></li>
            </ol>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 5: Verify Business Legitimacy</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Ensure the contractor operates a legitimate, established business:
          </p>

          <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
            <li><strong>Physical address:</strong> Visit or verify actual business location (not just P.O. box)</li>
            <li><strong>Business registration:</strong> Check Idaho Secretary of State business entity search</li>
            <li><strong>Tax ID:</strong> Legitimate businesses have federal EIN numbers</li>
            <li><strong>Years in business:</strong> Established contractors (3+ years) are lower risk</li>
            <li><strong>Professional website:</strong> Shows investment in business presence</li>
            <li><strong>Business phone:</strong> Not just personal cell phone</li>
            <li><strong>Email domain:</strong> Professional email (not @gmail.com)</li>
          </ul>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Step 6: Check for Legal Issues</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Research potential legal problems that could affect your project:
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-2">Check for Liens:</h4>
              <p className="text-gray-700">Search Ada County (or your county) recorder's office for mechanic's liens filed by or against the contractor. Liens indicate payment disputes.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-2">Court Records:</h4>
              <p className="text-gray-700">Search Idaho court records for lawsuits involving the contractor. Multiple lawsuits are a red flag.</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="font-bold text-gray-900 mb-2">Bankruptcy History:</h4>
              <p className="text-gray-700">Check PACER (federal court records) for bankruptcy filings. Recent bankruptcies indicate financial instability.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Red Flags That Should Make You Walk Away</h2>
          <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">Immediate Disqualifiers:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>🚫 <strong>No valid Idaho license</strong> or refuses to provide license number</li>
              <li>🚫 <strong>No insurance</strong> or won't provide proof of coverage</li>
              <li>🚫 <strong>Requests 50%+ upfront payment</strong> before work begins</li>
              <li>🚫 <strong>Won't provide written contract</strong> or estimate</li>
              <li>🚫 <strong>Pressure tactics</strong> ("This price is only good today")</li>
              <li>🚫 <strong>No physical address</strong> or only P.O. box</li>
              <li>🚫 <strong>Cash-only payments</strong> to avoid paper trail</li>
              <li>🚫 <strong>No references</strong> or won't provide contact information</li>
              <li>🚫 <strong>Won't pull permits</strong> for work that requires them</li>
              <li>🚫 <strong>Unlicensed subcontractors</strong> or won't disclose who's doing work</li>
              <li>🚫 <strong>Negative BBB rating</strong> with unresolved complaints</li>
              <li>🚫 <strong>Poor communication</strong> or unprofessional behavior</li>
            </ul>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Vetting Checklist: Print and Use</h2>
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4">Contractor Vetting Scorecard</h3>
            <div className="space-y-3 text-gray-700">
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Valid Idaho contractor license verified at ibol.idaho.gov</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>General liability insurance ($300,000+ coverage) verified</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Workers' compensation insurance verified (if applicable)</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>BBB rating checked (A- or better preferred)</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Google reviews checked (4+ stars, 20+ reviews)</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>3+ references contacted and satisfied</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Physical business address verified</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>No liens or legal issues found</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Written estimate and contract provided</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Reasonable payment terms (10-30% deposit)</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Professional communication and behavior</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="font-bold min-w-[30px]">☐</span>
                <span>Willing to pull permits and schedule inspections</span>
              </div>
            </div>
            <p className="mt-4 text-gray-700 font-semibold">✓ All boxes checked? You've found a qualified contractor!</p>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Contractor Scams in Idaho</h2>
          <div className="space-y-6 mb-8">
            <div className="border-l-4 border-red-600 pl-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">The "Storm Chaser"</h4>
              <p className="text-gray-700">After severe weather, unlicensed contractors go door-to-door offering quick repairs. They take deposits and disappear.</p>
            </div>
            <div className="border-l-4 border-red-600 pl-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">The "Leftover Materials" Scam</h4>
              <p className="text-gray-700">Contractor claims to have leftover materials from another job and offers a discount. Materials are often substandard or stolen.</p>
            </div>
            <div className="border-l-4 border-red-600 pl-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">The "Bait and Switch"</h4>
              <p className="text-gray-700">Low initial bid, then constant change orders and price increases once work begins.</p>
            </div>
            <div className="border-l-4 border-red-600 pl-6">
              <h4 className="text-xl font-bold text-gray-900 mb-2">The "Unlicensed Helper"</h4>
              <p className="text-gray-700">Licensed contractor sends unlicensed, uninsured workers to do the actual work.</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6 mb-12">
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How do I verify a contractor license in Idaho?</h3>
              <p className="text-gray-700">Visit ibol.idaho.gov and search by contractor name or license number. Verify the license is active, check for any disciplinary actions, and confirm it matches the contractor's specialty (RCE for residential, RCT for trades). You can also call (208) 334-3233 for verification.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What insurance should a contractor have in Idaho?</h3>
              <p className="text-gray-700">Idaho contractors must carry: General Liability Insurance (minimum $300,000 recommended), Workers' Compensation (if they have employees), and Commercial Auto Insurance (for work vehicles). Always request certificates of insurance and verify coverage is current.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How many references should I check before hiring a contractor?</h3>
              <p className="text-gray-700">Check at least 3-5 references from recent projects (within the last 12 months). Ask specific questions about quality, timeline, communication, budget adherence, and problem-solving. Also check online reviews on Google, Yelp, and Better Business Bureau.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">What are red flags when vetting a Boise contractor?</h3>
              <p className="text-gray-700">Red flags include: no valid license, no insurance proof, requesting large upfront payments (50%+), no written contract, pressure tactics, no physical address, cash-only payments, no references, and unwillingness to pull permits. Any of these should make you walk away.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Should I check a contractor's Better Business Bureau rating?</h3>
              <p className="text-gray-700">Yes, check BBB ratings at bbb.org. Look for: overall rating (A+ to F), number of complaints, how complaints were resolved, years in business, and any patterns in negative reviews. However, also check Google and Yelp for a complete picture.</p>
            </div>
            <div className="border-l-4 border-teal-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">How do I verify a contractor's insurance in Idaho?</h3>
              <p className="text-gray-700">Request a Certificate of Insurance (COI) directly from the contractor. Call the insurance company listed to verify coverage is active and adequate. The COI should list you as an additional insured for the project duration. Never accept expired or photocopied certificates.</p>
            </div>
          </div>

          <div className="bg-teal-600 text-white rounded-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Work With a Fully Vetted, Licensed Contractor</h2>
            <p className="text-xl mb-6">
              Fix Right Services is fully licensed, insured, and has an A+ BBB rating. We provide all credentials upfront and welcome your due diligence.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Request Free Estimate
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Related Articles:</h3>
            <ul className="space-y-2">
              <li>
                <a href="/blog/how-to-choose-remodeling-contractor-boise" className="text-teal-600 hover:underline">
                  How to Choose a Remodeling Contractor in Boise
                </a>
              </li>
              <li>
                <a href="/blog/how-to-get-home-improvement-estimate-boise" className="text-teal-600 hover:underline">
                  How to Get a Home Improvement Estimate in Boise
                </a>
              </li>
              <li>
                <a href="/blog/handyman-vs-contractor-idaho" className="text-teal-600 hover:underline">
                  Handyman vs. Contractor: Which Do You Need in Idaho?
                </a>
              </li>
            </ul>
          </div>
        </div>
      </article>
    </div>
  );
}